import type { HelpGuide } from '../types';

export const suppliersGuide: HelpGuide = {
  key: 'suppliers',
  title: 'ผู้ขาย (Suppliers)',
  subtitle: 'เมนู: ลูกหนี้ / เจ้าหนี้ ▸ ผู้ขาย (Suppliers)',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/suppliers/คู่มือการใช้งาน_ผู้ขาย-Suppliers_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ผู้ขาย (Suppliers)” คือทะเบียนข้อมูลหลักของเจ้าหนี้/ผู้ขายทั้งหมดของบริษัท ใช้สำหรับเพิ่ม-แก้ไขผู้ขาย เก็บข้อมูลนิติบุคคล สาขา บัญชีปลายทางสำหรับโอนเงิน บัญชีค่าใช้จ่ายเริ่มต้น และเงื่อนไขชำระ ข้อมูลที่บันทึกไว้ที่นี่จะถูกนำไปใช้ในงานตั้งหนี้ (AP) จ่ายเงิน และเอกสารที่เกี่ยวข้องกับผู้ขายทั่วทั้งระบบ',
        },
        {
          kind: 'callout',
          text: 'แนวคิดหลัก: ผู้ขาย 1 ราย = 1 นิติบุคคล (มีเลขประจำตัวผู้เสียภาษี 13 หลัก) และมีได้หลาย “สาขา” แต่ละสาขาเก็บที่อยู่ บัญชีปลายทาง เงื่อนไขชำระ และบัญชีค่าใช้จ่ายของตัวเอง · ระบบยังตรวจสอบข้อมูลผู้ขายเทียบกับฐานข้อมูลกรมสรรพากร (RD) และบังคับ “อนุมัติ 4 ตา” เมื่อต้องการเปลี่ยนบัญชีธนาคารปลายทาง',
        },
        { kind: 'para', text: 'หน้ารายการมี 2 แท็บสำหรับกรองตามสถานะใช้งาน:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ใช้งาน — ', text: 'แสดงเฉพาะผู้ขายที่ยังเปิดใช้งานอยู่ (แท็บเริ่มต้น)' },
            { lead: 'ทั้งหมด — ', text: 'แสดงผู้ขายทั้งหมด ทั้งที่ใช้งานและปิดใช้งานแล้ว' },
          ],
        },
        {
          kind: 'image',
          src: '/help/suppliers/01-list-overview.png',
          caption:
            'ภาพรวม — รายการผู้ขาย และแดชบอร์ดตรวจสอบสรรพากร (ยืนยันแล้ว/ชื่อไม่ตรง/ไม่ได้จด VAT/ยังไม่ตรวจ)',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        {
          kind: 'para',
          text: 'แถวบนสุดคือ “แดชบอร์ดตรวจสอบสรรพากร” แสดงจำนวนผู้ขายแยกตามผลตรวจกับกรมสรรพากร เป็นกลุ่ม ๆ ได้แก่ ยืนยันแล้ว · ต้องตรวจซ้ำ · ชื่อไม่ตรง · สาขาไม่ตรง · ไม่ได้จด VAT · ยังไม่ตรวจ — คลิกที่กลุ่มใดก็ได้เพื่อกรองรายการให้เหลือเฉพาะผู้ขายในกลุ่มนั้น',
        },
        { kind: 'para', text: 'ปุ่มและเครื่องมือบนหน้ารายการ:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ช่องค้นหา — ', text: 'ค้นหาผู้ขายจาก ชื่อผู้ขาย / เลขภาษี / รหัส' },
            { lead: 'นำเข้า — ', text: 'นำเข้าผู้ขายหลายรายพร้อมกันจากไฟล์ Excel/CSV' },
            { lead: 'เพิ่มผู้ขาย — ', text: 'เปิดฟอร์มเพิ่มผู้ขายใหม่ (ปุ่มสีหลัก)' },
            {
              lead: 'ช่องติ๊กเลือกหน้าแถว — ',
              text: 'เลือกผู้ขายหลายรายเพื่อสั่ง “ตรวจสอบกับสรรพากรอีกครั้ง” พร้อมกัน',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ตารางรายการมีคอลัมน์: รหัส · ชื่อผู้ขาย · เลขภาษี · สาขา (จำนวน) · ผลตรวจ RD · สถานะ — คลิกที่แถวใดก็ได้เพื่อเปิดหน้ารายละเอียดผู้ขาย ปุ่ม นำเข้า และ เพิ่มผู้ขาย จะกดได้ต่อเมื่อเลือกบริษัทแล้วเท่านั้น',
        },
      ],
    },
    {
      id: 'verification',
      heading: '3. การตรวจสอบกับกรมสรรพากร (RD) และการกรอง',
      blocks: [
        {
          kind: 'para',
          text: 'ระบบจะตรวจสอบเลขประจำตัวผู้เสียภาษีของผู้ขายเทียบกับฐานข้อมูลกรมสรรพากร เพื่อยืนยันว่าผู้ขายจดทะเบียน VAT จริง และชื่อ/สาขาตรงกันหรือไม่ ผลตรวจจะแสดงเป็นชิป “ผลตรวจ RD” ในแต่ละแถว และนับรวมเป็นกลุ่มในแดชบอร์ดด้านบน',
        },
        {
          kind: 'para',
          text: 'คลิกชิปกลุ่มในแดชบอร์ด (เช่น “ชื่อไม่ตรง”) เพื่อกรองรายการให้เหลือเฉพาะผู้ขายที่มีปัญหานั้น คลิกซ้ำที่กลุ่มเดิมเพื่อยกเลิกการกรอง วิธีนี้ช่วยให้ไล่แก้ผู้ขายที่ข้อมูลไม่ตรงสรรพากรได้ทีละกลุ่ม',
        },
        {
          kind: 'para',
          text: 'หากต้องการตรวจซ้ำ ให้ติ๊กเลือกผู้ขายที่ต้องการ (หรือติ๊กหัวตารางเพื่อเลือกทั้งหน้า) แล้วกดปุ่ม ตรวจสอบกับสรรพากรอีกครั้ง ระบบจะตรวจผลใหม่และอัปเดตชิปผลตรวจให้ทันที กด ยกเลิกการเลือก เพื่อล้างการเลือก',
        },
        {
          kind: 'image',
          src: '/help/suppliers/02-verification-filter.png',
          caption:
            'กรองตามผลตรวจ RD — คลิกชิป “ชื่อไม่ตรง” เพื่อแสดงเฉพาะผู้ขายที่ชื่อไม่ตรงสรรพากร',
        },
      ],
    },
    {
      id: 'create',
      heading: '4. การเพิ่มผู้ขายใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม เพิ่มผู้ขาย ที่มุมขวาบนของหน้ารายการ จะเปิดฟอร์ม “เพิ่มผู้ขายใหม่” ซึ่งกรอกได้ทั้งข้อมูลนิติบุคคลและสาขาแรกในครั้งเดียว',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'ชื่อผู้ขาย — ', text: 'ชื่อนิติบุคคล (จำเป็น)' },
            { lead: 'เลขภาษี — ', text: 'เลขประจำตัวผู้เสียภาษี 13 หลัก เช่น 0105500000001' },
            {
              lead: 'บัญชีเริ่มต้น — ',
              text: 'เลือกบัญชีค่าใช้จ่ายเริ่มต้น (ช่องค้นหาผังบัญชี กรองเฉพาะหมวดค่าใช้จ่าย)',
            },
            { lead: 'เงื่อนไขชำระ (terms) — ', text: 'เครดิตเทอม เช่น N30' },
            {
              lead: 'ส่วนสาขาแรก — ',
              text: 'รหัสสาขา (จำเป็น ปกติ 00000 = สำนักงานใหญ่) · ชื่อสาขา · ที่อยู่',
            },
          ],
        },
        {
          kind: 'para',
          text: 'เมื่อกรอกครบกดปุ่ม บันทึกผู้ขาย ในฟอร์ม ระบบจะสร้างผู้ขายพร้อมสาขาแรกให้ทันที ถ้าต้องการแก้ไขก่อนกดให้ใช้ปุ่ม ยกเลิก',
        },
        {
          kind: 'image',
          src: '/help/suppliers/03-create-supplier.png',
          caption:
            'โมดัลเพิ่มผู้ขายใหม่ — ข้อมูลนิติบุคคล (ชื่อ/เลขภาษี/บัญชีเริ่มต้น/เงื่อนไขชำระ) และสาขาแรก',
        },
      ],
    },
    {
      id: 'import',
      heading: '5. การนำเข้าผู้ขายจาก Excel/CSV',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม นำเข้า เพื่อเพิ่มผู้ขายหลายรายพร้อมกันจากไฟล์สเปรดชีต เหมาะกับการตั้งต้นข้อมูลจำนวนมาก ระบบจะให้ดาวน์โหลดเทมเพลตและตรวจความถูกต้องก่อนบันทึก',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'คอลัมน์จำเป็น — ',
              text: 'code (รหัสผู้ขาย) · nameTh (ชื่อผู้ขาย) · taxId (เลขประจำตัวผู้เสียภาษี)',
            },
            { lead: 'คอลัมน์เพิ่มเติม — ', text: 'branchCode (รหัสสาขา) · terms (เงื่อนไขชำระ)' },
          ],
        },
        {
          kind: 'para',
          text: 'ระบบจะตรวจเลขภาษี 13 หลักด้วยหลักตรวจสอบ (mod-11) ตรวจรายการซ้ำภายในไฟล์ และซ้ำกับผู้ขายที่มีอยู่แล้วในระบบ แถวที่มีปัญหาจะถูกทำเครื่องหมายเป็นข้อผิดพลาด การนำเข้าเป็นการ “สร้างใหม่” เท่านั้น (ไม่แก้ไขรายเดิม) เมื่อเสร็จจะสรุปจำนวนที่สำเร็จ/ล้มเหลวพร้อมรายละเอียด',
        },
        {
          kind: 'image',
          src: '/help/suppliers/04-import-supplier.png',
          caption: 'นำเข้าผู้ขายจาก Excel/CSV — ตารางคอลัมน์ที่ต้องใช้ และเทมเพลตสำหรับเตรียมไฟล์',
        },
      ],
    },
    {
      id: 'detail',
      heading: '6. หน้ารายละเอียดผู้ขาย (ภาพรวม 360)',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกผู้ขายในรายการเพื่อเปิดหน้า “รายละเอียดผู้ขาย” ที่นี่แสดงชื่อผู้ขายพร้อมชิปผลตรวจ RD ตารางสาขาทั้งหมด (รหัสสาขา · ชื่อสาขา · ที่อยู่ · สถานะ) และส่วนแก้ไขแบบเร็วของแต่ละสาขาสำหรับ เครดิตเทอม / บัญชีเริ่มต้น / อีเมล',
        },
        {
          kind: 'para',
          text: 'แผง “ภาพรวม 360” สรุปสถานะการเงินกับผู้ขายรายนี้: ยอดค้างชำระ (Open AP) · ยอดซื้อสะสมปีนี้ (YTD) · วันที่ชำระล่าสุด พร้อมกราฟวิเคราะห์อายุหนี้ (ยังไม่ถึงกำหนด / 1-30 / 31-60 / 61-90 / 90+ วัน) และตารางเอกสารล่าสุดที่เกี่ยวข้องกับผู้ขาย',
        },
        {
          kind: 'para',
          text: 'ปุ่มบนหน้านี้: แก้ไขผู้ขาย (แก้ค่าตั้งต้นระดับนิติบุคคล) · เพิ่มสาขา (เพิ่มสาขาใหม่) · แก้ไข ที่ท้ายแต่ละแถวสาขา · ประวัติการแก้ไข (ดูบันทึกการเปลี่ยนแปลง) · กลับรายการ เพื่อย้อนกลับหน้ารายการ',
        },
        {
          kind: 'image',
          src: '/help/suppliers/05-supplier-detail.png',
          caption:
            'รายละเอียดผู้ขาย — สาขา/บัญชีปลายทาง และภาพรวม 360 (ยอดค้างชำระ/อายุหนี้/เอกสารล่าสุด)',
        },
      ],
    },
    {
      id: 'vendor-bank',
      heading: '7. บัญชีธนาคารปลายทางของผู้ขาย',
      blocks: [
        {
          kind: 'para',
          text: 'แต่ละสาขามีส่วน “บัญชีธนาคารปลายทาง” แสดงบัญชีที่ใช้โอนเงินจ่ายผู้ขาย เพื่อความปลอดภัยเลขบัญชีจะถูกปกปิดบางส่วน (แสดงเฉพาะเลขท้าย เช่น ••••1234) และ ไม่สามารถแก้ไขโดยตรงได้',
        },
        {
          kind: 'callout',
          text: 'สำคัญ: การเปลี่ยนบัญชีปลายทางต้องทำผ่าน “คำขอเปลี่ยนบัญชีธนาคาร” และผ่านการอนุมัติแบบ 4 ตาเท่านั้น ไม่มีทางลัดแก้เลขบัญชีตรง ๆ เพื่อป้องกันการสวมบัญชี/โอนเงินผิดราย',
        },
        {
          kind: 'para',
          text: 'ในส่วนนี้จะเห็นรายการคำขอเปลี่ยนบัญชีของสาขานั้นพร้อมสถานะแต่ละคำขอ (ฉบับร่าง / รออนุมัติ / เปลี่ยนแล้ว / ไม่อนุมัติ / ตีกลับแก้ไข) ปุ่ม ขอเปลี่ยนบัญชีธนาคาร จะแสดงเฉพาะผู้ใช้ที่มีสิทธิ (vendorbank.request)',
        },
        {
          kind: 'image',
          src: '/help/suppliers/06-vendor-bank-section.png',
          caption: 'บัญชีธนาคารปลายทาง — เลขบัญชีปกปิด พร้อมรายการคำขอเปลี่ยนบัญชีและสถานะ',
        },
      ],
    },
    {
      id: 'bank-change-request',
      heading: '8. การขอเปลี่ยนบัญชีธนาคาร',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ขอเปลี่ยนบัญชีธนาคาร เพื่อเปิดฟอร์มเสนอบัญชีปลายทางใหม่ ระบุข้อมูลที่ต้องการเปลี่ยน (ช่องที่ไม่กรอกจะใช้ค่าเดิม)',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'เลขบัญชีใหม่ — ', text: 'เลขบัญชีปลายทางใหม่' },
            { lead: 'ธนาคารใหม่ — ', text: 'เช่น KBANK / BBL / SCB' },
            { lead: 'รหัสสาขาธนาคาร — ', text: 'สาขาของธนาคารปลายทาง (ถ้ามี)' },
            { lead: 'ชื่อบัญชีใหม่ — ', text: 'ชื่อเจ้าของบัญชี' },
            { lead: 'ประเภท — ', text: 'ประเภทบัญชี (ออมทรัพย์ / กระแสรายวัน ฯลฯ)' },
            { lead: 'เหตุผล (ถ้ามี) — ', text: 'เหตุผลที่ต้องเปลี่ยนบัญชี' },
          ],
        },
        {
          kind: 'callout',
          text: 'คำขอจะถูกส่งให้ผู้อนุมัติ (4 ตา) — บัญชีจะเปลี่ยนเมื่ออนุมัติแล้วเท่านั้น เมื่อกด ส่งคำขออนุมัติ คำขอจะเปลี่ยนสถานะจากฉบับร่างเป็น “รออนุมัติ” ทันที',
        },
        {
          kind: 'image',
          src: '/help/suppliers/07-bank-change-request.png',
          caption: 'โมดัลขอเปลี่ยนบัญชีธนาคาร — กรอกบัญชีใหม่ ก่อนส่งเข้าสู่การอนุมัติ 4 ตา',
        },
      ],
    },
    {
      id: 'bank-change-approval',
      heading: '9. การอนุมัติเปลี่ยนบัญชีธนาคาร',
      blocks: [
        {
          kind: 'para',
          text: 'ผู้มีสิทธิอนุมัติ (vendorbank.approve) จะเห็นแผงอนุมัติเมื่อคลิกคำขอที่ “รออนุมัติ” แผงนี้เปรียบเทียบบัญชี เดิม → ใหม่ โดยเน้นช่องที่เปลี่ยนแปลง พร้อมไทม์ไลน์ขั้นตอนการอนุมัติ (ผู้จัดทำ → ผู้อนุมัติ)',
        },
        {
          kind: 'para',
          text: 'ก่อนอนุมัติ ผู้อนุมัติต้อง “ยืนยันนอกระบบ” กับผู้ขายก่อน (โทรศัพท์ / จดหมาย / อีเมลถึงผู้ขาย) เพื่อยืนยันว่าบัญชีใหม่ถูกต้องจริง — ปุ่มพิจารณาจะเปิดให้กดได้ก็ต่อเมื่อบันทึกการยืนยันนอกระบบแล้ว จากนั้นจึงเลือก อนุมัติ / ตีกลับแก้ไข / ไม่อนุมัติ เมื่ออนุมัติขั้นสุดท้าย ระบบจึงเขียนทับบัญชีปลายทางจริงและบันทึกลงประวัติการแก้ไข',
        },
        {
          kind: 'image',
          src: '/help/suppliers/08-bank-change-approval.png',
          caption:
            'แผงอนุมัติเปลี่ยนบัญชี — เปรียบเทียบบัญชีเดิม→ใหม่ และการยืนยันนอกระบบก่อนอนุมัติ',
        },
      ],
    },
    {
      id: 'change-history',
      heading: '10. ประวัติการแก้ไขผู้ขาย',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ประวัติการแก้ไข ที่มุมขวาบนของหน้ารายละเอียด เพื่อดูบันทึกการเปลี่ยนแปลงข้อมูลผู้ขายแบบเพิ่มต่อท้ายอย่างเดียว (ลบ/แก้ไม่ได้) ใช้ตรวจสอบย้อนหลังว่าใครแก้อะไร เมื่อใด',
        },
        {
          kind: 'para',
          text: 'แต่ละรายการแสดงชื่อช่องที่แก้ ค่าเดิม (ขีดฆ่า) → ค่าใหม่ (ตัวหนา) พร้อมผู้แก้ไข (หรือ “ระบบ”) และวันที่ ถ้าเป็นการแก้ระดับสาขาจะมีป้าย “สาขา” กำกับ การเปลี่ยนบัญชีธนาคารที่อนุมัติแล้วก็จะถูกบันทึกไว้ในประวัตินี้ด้วย',
        },
        {
          kind: 'image',
          src: '/help/suppliers/09-change-history.png',
          caption: 'ประวัติการแก้ไขผู้ขาย — ค่าเดิม→ค่าใหม่ พร้อมผู้แก้ไขและวันที่',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '11. สรุปสถานะและผลตรวจ',
      blocks: [
        { kind: 'para', text: 'สถานะการใช้งานของผู้ขาย/สาขา:' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'ผู้ขาย/สาขาที่เปิดใช้งานอยู่ ใช้ในงานตั้งหนี้/จ่ายเงินได้'],
            ['ปิดใช้งาน', 'ปิดใช้งานแล้ว ไม่แสดงในแท็บ “ใช้งาน”'],
          ],
        },
        { kind: 'para', text: 'ชิปผลตรวจกับกรมสรรพากร (RD):' },
        {
          kind: 'table',
          head: ['ผลตรวจ RD', 'ความหมาย'],
          rows: [
            ['ตรงกับสรรพากร', 'เลขภาษี ชื่อ และสาขา ตรงกับฐานข้อมูลสรรพากร'],
            ['ชื่อไม่ตรง', 'พบเลขภาษีแต่ชื่อไม่ตรง — ควรตรวจสอบ/แก้ไข'],
            ['สาขาไม่ตรง', 'ชื่อตรงแต่สาขาไม่ตรง'],
            ['ไม่ได้จด VAT', 'ไม่พบการจดทะเบียน VAT ในฐานข้อมูลสรรพากร'],
            ['เลขภาษีไม่ถูกต้อง', 'เลขประจำตัวผู้เสียภาษีไม่ถูกต้อง'],
            ['ตรวจไม่ได้', 'ระบบตรวจไม่สำเร็จในขณะนั้น'],
            ['ยังไม่ตรวจ', 'ยังไม่เคยตรวจกับสรรพากร'],
          ],
        },
        { kind: 'para', text: 'สถานะคำขอเปลี่ยนบัญชีธนาคาร:' },
        {
          kind: 'table',
          head: ['สถานะคำขอ', 'ความหมาย'],
          rows: [
            ['ฉบับร่าง', 'สร้างคำขอแล้วแต่ยังไม่ส่งอนุมัติ'],
            ['รออนุมัติ', 'ส่งแล้ว รอผู้อนุมัติยืนยันนอกระบบและอนุมัติ'],
            ['เปลี่ยนแล้ว', 'อนุมัติครบ บัญชีปลายทางถูกเปลี่ยนแล้ว'],
            ['ไม่อนุมัติ', 'ผู้อนุมัติปฏิเสธ บัญชีไม่เปลี่ยน'],
            ['ตีกลับแก้ไข', 'ส่งคืนให้ผู้จัดทำแก้ไขแล้วยื่นใหม่'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมแก้เลขบัญชีธนาคารปลายทางตรง ๆ ไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'บัญชีปลายทางเป็นข้อมูลอ่อนไหวด้านการเงิน ต้องเปลี่ยนผ่านคำขอ + อนุมัติ 4 ตา + ยืนยันนอกระบบ เพื่อป้องกันการสวมบัญชี',
            },
          ],
        },
        { kind: 'para', text: 'ผลตรวจ RD ขึ้น “ชื่อไม่ตรง” ต้องทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'คลิกชิปกลุ่ม “ชื่อไม่ตรง” เพื่อกรองดูทั้งหมด แก้ชื่อผู้ขายให้ตรงทะเบียนสรรพากร แล้วเลือกผู้ขายกด “ตรวจสอบกับสรรพากรอีกครั้ง”',
            },
          ],
        },
        { kind: 'para', text: 'ผู้ขายรายเดียวกันมีหลายสาขาทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เปิดหน้ารายละเอียดผู้ขาย แล้วกดปุ่ม “เพิ่มสาขา” แต่ละสาขาเก็บที่อยู่ บัญชีปลายทาง และเงื่อนไขชำระแยกกัน',
            },
          ],
        },
        { kind: 'para', text: 'นำเข้าผู้ขายแล้วบางแถวขึ้น error?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'มักเกิดจากเลขภาษีไม่ถูกต้อง (mod-11) หรือซ้ำกับรายในไฟล์/ในระบบ — แก้ในไฟล์แล้วนำเข้าใหม่เฉพาะแถวที่ผิด',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ผู้ขาย (Supplier) — ',
              text: 'นิติบุคคลเจ้าหนี้ 1 ราย มีเลขประจำตัวผู้เสียภาษีเดียว',
            },
            {
              lead: 'สาขา (Branch/Vendor) — ',
              text: 'หน่วยย่อยของผู้ขาย เก็บที่อยู่ บัญชีปลายทาง และเงื่อนไขชำระของตัวเอง',
            },
            { lead: 'ผลตรวจ RD — ', text: 'ผลการตรวจข้อมูลผู้ขายเทียบกับฐานข้อมูลกรมสรรพากร' },
            {
              lead: 'บัญชีเริ่มต้น — ',
              text: 'บัญชีค่าใช้จ่ายในผังบัญชีที่ใช้ตั้งต้นเวลาตั้งหนี้ผู้ขายรายนี้',
            },
            { lead: 'เงื่อนไขชำระ (terms) — ', text: 'เครดิตเทอม เช่น N30 = ชำระภายใน 30 วัน' },
            {
              lead: 'บัญชีธนาคารปลายทาง — ',
              text: 'บัญชีที่บริษัทใช้โอนเงินจ่ายผู้ขาย (เลขปกปิด เปลี่ยนผ่าน 4 ตา)',
            },
            {
              lead: 'อนุมัติ 4 ตา — ',
              text: 'การแยกผู้จัดทำกับผู้อนุมัติ ต้องมีคนละคนตรวจ/อนุมัติเพื่อความปลอดภัย',
            },
            {
              lead: 'ยืนยันนอกระบบ — ',
              text: 'การยืนยันบัญชีใหม่กับผู้ขายผ่านโทรศัพท์/จดหมาย/อีเมล ก่อนอนุมัติ',
            },
            {
              lead: 'ภาพรวม 360 — ',
              text: 'สรุปยอดค้างชำระ อายุหนี้ ยอดซื้อสะสม และเอกสารล่าสุดของผู้ขาย',
            },
          ],
        },
      ],
    },
  ],
};
